import * as Sentry from "@sentry/react";
import axios from "axios";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

const isAbortedRequest = (err: unknown): boolean =>
  axios.isCancel(err) ||
  (axios.isAxiosError(err) && err.message === "Request aborted");

const canonicalizeQueryHash = (queryHash: string): string => {
  let arr: unknown[];

  try {
    arr = JSON.parse(queryHash);
    if (!Array.isArray(arr)) {
      // fallback if parsing doesn't return an array
      arr = [queryHash];
    }
  } catch {
    // fallback if not JSON
    arr = [queryHash];
  }

  const uuidRegex =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

  // Replace UUIDs in each element, only if it's a string; if number, replace with "number"
  const replaced = arr.map((el: unknown) => {
    if (typeof el === "string") {
      return el.replace(uuidRegex, "UUID");
    } else if (typeof el === "number") {
      return "number";
    }
    return el;
  });

  // Collapse consecutive "UUID"s in a single string element
  const collapsed = replaced.map((el: unknown) =>
    typeof el === "string" ? el.replace(/(?:UUID,)+UUID/g, "UUIDs") : el
  );

  return JSON.stringify(collapsed);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        console.log({ failureCount, error });
        const axiosError = error as { response?: { status?: number } };
        if (
          // Don't alert on 400 Bad Request as we get this from preview requests which contain an invalid value
          axiosError.response?.status === 400 ||
          axiosError.response?.status === 404 ||
          // Don't alert on 503 Service Unavailable as we get that when battery requests timeout
          axiosError.response?.status === 503 ||
          failureCount > 3
        ) {
          return false;
        }
        return true;
      },
    },
  },
  // Hook the MutationCache and QueryCache so that individual queries can
  // still set onError if they want to, but we have a fallback to log
  // unhandled errors to Sentry
  // https://aronschueler.de/blog/2022/12/16/generating-meaningful-issues-in-sentry-with-react-query-+-axios/
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      if (isAbortedRequest(err)) return;
      const axiosError = err as { response?: { status?: number } };
      if (
        // Don't alert on 400 Bad Request as we get this from preview requests which contain an invalid value
        axiosError.response?.status === 400 ||
        // Don't alert on 503 Service Unavailable as we get that when battery requests timeout
        axiosError.response?.status === 503
      ) {
        return;
      }
      Sentry.withScope((scope) => {
        scope.setContext("mutation", {
          mutationId: mutation.mutationId,
          variables: mutation.state.variables,
        });
        if (mutation.options.mutationKey) {
          scope.setFingerprint(
            // Duplicate to prevent modification
            Array.from(mutation.options.mutationKey as string[])
          );
        }
        Sentry.captureException(err);
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (isAbortedRequest(err)) return;
      const axiosError = err as { response?: { status?: number } };
      if (
        // Don't alert on 400 Bad Request as we get this from preview requests which contain an invalid value
        axiosError.response?.status === 400 ||
        // Don't alert on 503 Service Unavailable as we get that when battery requests timeout
        axiosError.response?.status === 503
      ) {
        return;
      }
      Sentry.withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash });
        scope.setFingerprint([canonicalizeQueryHash(query.queryHash)]);
        Sentry.captureException(err);
      });
    },
  }),
});

export default queryClient;
